import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantCollection } from '../hooks/useTenantData';
import { useClub } from '../contexts/ClubContext';

function KitShop() {
  const navigate = useNavigate();
  const { club } = useClub();
  const [cart, setCart] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState({});
  
  // Use tenant-scoped data hook
  const { data: tenantProducts, loading } = useTenantCollection('products');

  // Default products (fallback if Firestore is empty)
  const defaultProducts = [
    {
      id: 1,
      name: "Home Jersey 2026",
      category: "Jerseys",
      price: 350,
      image: "https://images.unsplash.com/photo-1580087256394-dc596e1c8f4f?w=400&h=400&fit=crop",
      sizes: ["YS", "YM", "YL", "S", "M", "L", "XL"],
      description: "Official ClubLink TT home jersey with embroidered crest"
    },
    {
      id: 2,
      name: "Away Jersey 2026",
      category: "Jerseys",
      price: 350,
      image: "https://images.unsplash.com/photo-1622290291468-a28f7a7dc6a8?w=400&h=400&fit=crop",
      sizes: ["YS", "YM", "YL", "S", "M", "L", "XL"],
      description: "Official ClubLink TT away jersey with embroidered crest"
    },
    {
      id: 3,
      name: "Training Jersey",
      category: "Training",
      price: 250,
      image: "https://images.unsplash.com/photo-1565462905097-5e701408bb99?w=400&h=400&fit=crop",
      sizes: ["YS", "YM", "YL", "S", "M", "L", "XL"],
      description: "Lightweight breathable training jersey"
    },
    {
      id: 4,
      name: "Match Shorts",
      category: "Shorts",
      price: 150,
      image: "https://images.unsplash.com/photo-1591195853828-11db59a44f6b?w=400&h=400&fit=crop",
      sizes: ["YS", "YM", "YL", "S", "M", "L", "XL"],
      description: "Official match day shorts with club logo"
    },
    {
      id: 5,
      name: "Training Shorts",
      category: "Shorts",
      price: 120,
      image: "https://images.unsplash.com/photo-1562886877-f12251816e01?w=400&h=400&fit=crop",
      sizes: ["YS", "YM", "YL", "S", "M", "L", "XL"],
      description: "Comfortable training shorts for practice sessions"
    },
    {
      id: 6,
      name: "Club Socks",
      category: "Accessories",
      price: 60,
      image: "https://images.unsplash.com/photo-1586350977771-b3b0abd50c82?w=400&h=400&fit=crop",
      sizes: ["YS", "YM", "YL", "S", "M", "L"],
      description: "Official match socks with ClubLink TT colors"
    },
    {
      id: 7,
      name: "Training Bag",
      category: "Accessories",
      price: 200,
      image: "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400&h=400&fit=crop",
      sizes: ["One Size"],
      description: "Spacious training bag with multiple compartments"
    },
    {
      id: 8,
      name: "Club Cap",
      category: "Accessories",
      price: 80,
      image: "https://images.unsplash.com/photo-1588850561407-ed78c282e89b?w=400&h=400&fit=crop",
      sizes: ["One Size"],
      description: "Adjustable cap with embroidered club logo"
    }
  ];

  // Use tenant products if available (filtered for in-stock), otherwise use defaults
  const inStockTenantProducts = tenantProducts.filter(p => p.inStock !== false);
  const products = inStockTenantProducts.length > 0 ? inStockTenantProducts : defaultProducts;

  const handleSizeSelect = (productId, size) => {
    setSelectedSizes({ ...selectedSizes, [productId]: size });
  };

  const addToCart = (product) => {
    const size = selectedSizes[product.id];
    if (!size) {
      alert("Please select a size first!");
      return;
    }
    
    const cartItem = {
      ...product,
      size: size,
      cartId: Date.now()
    };
    
    setCart([...cart, cartItem]);
    alert(`${product.name} (${size}) added to cart!`);
  };

  const cartTotal = cart.reduce((total, item) => total + item.price, 0);

  if (loading) {
    return (
      <div className="kitshop-page">
        <div className="shop-hero">
          <h1>KIT SHOP</h1>
          <p>Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="kitshop-page">
      {/* Hero Banner */}
      <div className="shop-hero">
        <h1>KIT SHOP</h1>
        <p>Official {club?.name || 'ClubLink TT'} gear for players and supporters</p>
      </div>

      {/* Cart Summary */}
      <div className="cart-summary">
        <div className="cart-icon">🛒</div>
        <span className="cart-count">{cart.length} items</span>
        <span className="cart-total">TT$ {cartTotal.toLocaleString()}</span>
        {cart.length > 0 && (
          <button 
            className="checkout-btn"
            onClick={() => navigate('/checkout', { state: { cart } })}
          >
            CHECKOUT →
          </button>
        )}
      </div>

      {/* Products Grid */}
      <div className="products-container">
        <div className="products-grid">
          {products.map((product) => (
            <div className="product-card" key={product.id}>
              <div className="product-image">
                <img src={product.image} alt={product.name} />
                <span className="product-category">{product.category}</span>
              </div>
              
              <div className="product-info">
                <h3 className="product-name">{product.name}</h3>
                <p className="product-description">{product.description}</p>
                <div className="product-price">TT$ {product.price}</div>
                
                <div className="size-selector">
                  <span className="size-label">Size:</span>
                  <div className="size-options">
                    {product.sizes.map((size) => (
                      <button
                        key={size}
                        className={`size-btn ${selectedSizes[product.id] === size ? 'selected' : ''}`}
                        onClick={() => handleSizeSelect(product.id, size)}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                <button 
                  className="add-to-cart-btn"
                  onClick={() => addToCart(product)}
                >
                  ADD TO CART
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Info Section */}
      <div className="shop-info">
        <div className="info-card">
          <div className="info-icon">🚚</div>
          <h4>Local Delivery</h4>
          <p>Island-wide delivery available</p>
        </div>
        <div className="info-card">
          <div className="info-icon">💳</div>
          <h4>Secure Payment</h4>
          <p>Safe and secure checkout</p>
        </div>
        <div className="info-card">
          <div className="info-icon">↩️</div>
          <h4>Easy Returns</h4>
          <p>14-day return policy</p>
        </div>
      </div>
    </div>
  );
}

export default KitShop;